<script setup>
import { useI18n } from 'vue-i18n';

import { alertDialog } from '../composables/useDialog';
import { HrIconBtn } from '../primitives';

/**
 * Icon button that opens a modal hint dialog when activated.
 *
 * The middle of three tiers of help text. One sentence the reader needs to decide is supporting
 * text under the control's label (a field's `hint`, a setting row's `description`); a longer or
 * occasional explanation comes here; a tooltip only names an icon and never carries help.
 *
 * ALWAYS VISIBLE. A button faded in on hover goes unnoticed by a reader who never points at the
 * spot, and on touch there is no hover to summon it.
 *
 * @status recommended
 * @group actions
 * @useWhen An explanation longer than a sentence, or one most readers need only occasionally
 * @avoidWhen One sentence the reader needs to decide: use the control's supporting text instead
 * @related HrTooltip
 * @related HrIconActionBtn
 * @a11y Announced as "Help"; a caller's own aria-label overrides it. The hint text is presented in
 *   an alert dialog when activated.
 */
defineOptions({ name: 'HrIconHintBtn' });

const props = defineProps({
  /** Help text displayed in the dialog when the button is activated. */
  hint: { type: String, required: true },
});

const { t } = useI18n();

function onShowDialogTooltip() {
  alertDialog(null, props.hint);
}

// @todo Remove legacy-props when Legacy theme is removed
const legacyProps = {
  dense: true,
  round: true,
  flat: true,
  size: 'sm',
};
</script>

<template>
  <hr-icon-btn
    variant="standard"
    size="xs"
    icon="help"
    :aria-label="t('common.buttonHelp')"
    :legacy-props="legacyProps"
    v-bind="$props"
    @click="onShowDialogTooltip"
  >
    <!-- @slot Content forwarded to the underlying icon button. -->
    <slot />
  </hr-icon-btn>
</template>
